/**
 * Interpolates a match score (0–100) to an RGB colour along JobCrawls jc / earth-tone stops.
 * Light palette is tuned for cards on `jc-surface` / white; dark for chocolate card backgrounds.
 */
import { jcThemeHex } from '../../constants/jcThemeHex';

const TAN = '#C4A882';

type Stop = readonly [number, string];

const LIGHT_STOPS: Stop[] = [
  [0, jcThemeHex.light.filterCompanyBorder],
  [25, TAN],
  [50, jcThemeHex.light.gold],
  [75, jcThemeHex.light.accentSoft],
  [100, jcThemeHex.light.accent],
];

const DARK_STOPS: Stop[] = [
  [0, jcThemeHex.dark.filterCompanyBorder],
  [25, jcThemeHex.dark.muted],
  [50, jcThemeHex.dark.gold],
  [75, jcThemeHex.dark.accentSoft],
  [100, jcThemeHex.dark.accent],
];

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '').trim();
  if (h.length !== 6) {
    throw new Error(`getMatchDonutScoreColor: expected #RRGGBB, got ${hex}`);
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function lerpByte(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function lerpHex(c0: string, c1: string, t: number): string {
  const a = hexToRgb(c0);
  const b = hexToRgb(c1);
  const u = Math.max(0, Math.min(1, t));
  return `rgb(${lerpByte(a.r, b.r, u)}, ${lerpByte(a.g, b.g, u)}, ${lerpByte(a.b, b.b, u)})`;
}

export function getMatchDonutScoreColor(score: number, mode: 'light' | 'dark'): string {
  const n = typeof score === 'number' && Number.isFinite(score) ? score : 0;
  const clamped = Math.max(0, Math.min(100, n));
  const stops = mode === 'dark' ? DARK_STOPS : LIGHT_STOPS;

  for (let i = 0; i < stops.length - 1; i++) {
    const [s0, c0] = stops[i];
    const [s1, c1] = stops[i + 1];
    if (clamped <= s1) {
      const t = s1 === s0 ? 0 : (clamped - s0) / (s1 - s0);
      return lerpHex(c0, c1, t);
    }
  }

  return lerpHex(stops[stops.length - 2][1], stops[stops.length - 1][1], 1);
}
